/**
 * Firebase Configuration
 * Initialize Firebase services for authentication and database
 *
 * IMPORTANT: Uses memory-only cache with eager garbage collection to prevent
 * "FIRESTORE INTERNAL ASSERTION FAILED" errors. Eager GC only releases target
 * state when all listeners are removed, preventing the watch stream state
 * corruption that LRU GC can cause by evicting active targets.
 */

import { initializeApp, getApp, getApps } from 'firebase/app';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import { initializeFirestore, memoryLocalCache, memoryEagerGarbageCollector, getFirestore, type Firestore, terminate } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

/**
 * Clear stale Firestore IndexedDB databases left over from previous persistence configurations.
 * With memory-only cache, Firestore no longer creates IndexedDB databases,
 * but old ones from before the migration may still exist and should be cleaned up.
 */
function deleteDatabaseSafely(name: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
      request.onblocked = () => resolve();
    } catch {
      resolve();
    }
  });
}

async function clearStaleFirestoreIndexedDB(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;

  try {
    const databases = await indexedDB.databases?.();
    if (databases && databases.length > 0) {
      const deletePromises = databases
        .filter((db) => db.name && db.name.includes('firestore'))
        .map((db) => deleteDatabaseSafely(db.name!));
      if (deletePromises.length > 0) {
        await Promise.all(deletePromises);
      }
      return;
    }
  } catch {
    // Ignore and fall back to common names
  }

  const commonNames = [
    'firestore/[DEFAULT]/luciddock-d1d70/main',
    'firestore/[DEFAULT]/luciddock-v2/main',
  ];
  await Promise.all(commonNames.map((name) => deleteDatabaseSafely(name)));
}

// Clear stale databases before Firebase initialization
clearStaleFirestoreIndexedDB().catch(() => {
  // Ignore cleanup errors - not critical for app function
});

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase (avoid re-initialization during HMR)
const isFirstInit = getApps().length === 0;
const app = isFirstInit ? initializeApp(firebaseConfig) : getApp();

// Initialize Firebase services
export const auth = getAuth(app);

// Initialize Firestore with memory-only cache and eager garbage collection.
// Eager GC releases query results immediately when all listeners are removed,
// preventing the state corruption that LRU GC causes when it evicts targets
// with active listeners (which leads to ve:-1 assertion failures).
let firestoreInstance: Firestore;

try {
  if (isFirstInit) {
    firestoreInstance = initializeFirestore(app, {
      localCache: memoryLocalCache({
        garbageCollector: memoryEagerGarbageCollector(),
      }),
    });
  } else {
    // HMR or hot reload - reuse existing instance
    firestoreInstance = getFirestore(app);
  }
} catch {
  // If initialization fails for any reason, try to get existing instance
  firestoreInstance = getFirestore(app);
}
export const db = firestoreInstance;

export const storage = getStorage(app);
export const googleProvider = new GoogleAuthProvider();

// Export the app instance
export default app;

/**
 * Reset Firestore connection and clear any lingering IndexedDB databases.
 * Used as a last-resort recovery when Firestore reports internal assertion errors.
 *
 * With memory-only cache, we only need to terminate and restart the connection.
 * clearIndexedDbPersistence is NOT called because it fails when persistence is disabled.
 */
export async function resetFirestoreCache(): Promise<void> {
  try {
    await terminate(db);
  } catch {
    // Ignore terminate failures
  }

  await clearStaleFirestoreIndexedDB();
}
